import {
  Checkbox,
  CheckboxSelect,
  DefaultModalDialog,
  DefaultNumberInputField,
  InputField,
  LabeledText,
  Text,
} from '~/common/components/defaultUIComponents';
import {
  Flex,
  Stack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
} from '@chakra-ui/react';
import { FormikCheckbox } from '~/common/components/defaultUIComponents/FormikCheckbox';
import {
  Employee,
  WorkDay,
} from '~/common/entities/Department/DepartmentTypes';
import { EmployeePatchRequest } from '~/common/entities/Employees/EmployeeRequest';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import { PasswordSchema } from '~/common/validators/PasswordValidator';
import { durationToString } from '~/common/utils/durationUtils';
import { EmployeeDepartments } from './EmployeeDepartments';
import useTranslate from '~/common/utils/translate';
import { formatEmployeeUsername } from './utils';
import { Field, FieldProps } from 'formik';
import React, { useState } from 'react';
import moment from 'moment';
import * as yup from 'yup';

type Props = {
  employee: Employee;
  isOpen: boolean;
  onToggle: () => void;
};

interface InputProps {
  work_hours_per_day?: number;
  password?: string;
  needs_two_factor?: boolean;
  work_days: WorkDay[];
  first_name: string;
  last_name: string;
  departments: number[];
  dailyBreakTime: number;
}

const EmployeeSchema = yup.object({
  first_name: yup.string().required('validator.required'),
  last_name: yup.string().required('validator.required'),
  work_hours_per_day: yup
    .number()
    .required('validator.required')
    .min(0)
    .max(24),
  password: PasswordSchema,
  needs_two_factor: yup.boolean(),
  departments: yup
    .array()
    .of(yup.number().required('validator.required'))
    .required('validator.required')
    .min(1, 'validator.error.chooseOne'),
});

export const EditEmployeeModal: React.FC<Props> = ({
  employee,
  isOpen,
  onToggle,
}) => {
  const translate = useTranslate();
  const { data: allRoles } = EmployeeApi.hooks.useRoles();
  const { data: roles } = EmployeeApi.hooks.useRolesOfEmployee(employee);
  const { mutate: addRole } = EmployeeApi.hooks.useAddRole();
  const { mutateAsync: editEmployee } = EmployeeApi.hooks.useEditEmployee();

  const [changePassword, setChangePassword] = useState(false);
  const workDayValues = Object.values(WorkDay).slice(
    0,
    Object.values(WorkDay).length,
  ) as string[];
  return (
    <DefaultModalDialog
      dialogHeaderText={
        <>
          <Text>employeeTile.editEmployee.dialogHeaderText</Text>
          <LabeledText mt={0} label="employeeTile.editEmployee.username">
            {formatEmployeeUsername(employee)}
          </LabeledText>
        </>
      }
      submitText="employeeTile.editEmployee.submitText"
      isOpen={isOpen}
      onCancel={onToggle}
      onSubmit={(values: InputProps) => {
        const request: EmployeePatchRequest = {
          work_hours_per_day: durationToString(
            moment.duration(values.work_hours_per_day, 'hours'),
          ),
          password: values.password,
          needs_two_factor: values.needs_two_factor ?? false,
          work_days: values.work_days,
          first_name: values.first_name,
          last_name: values.last_name,
          departments: values.departments,
          dailyBreakTime: values.dailyBreakTime,
        };
        console.log('Employee Patch Request:', request);
        return editEmployee({ employee, request }).then(onToggle);
      }}
      showCancelButton
      size="xl"
      initialValues={
        {
          work_days: employee.work_days,
          work_hours_per_day: moment
            .duration(employee.work_hours_per_day)
            ?.get('hours'),
          needs_two_factor: employee.needs_two_factor,
          first_name: employee.user.first_name,
          last_name: employee.user.last_name,
          departments: employee.departments as unknown as number[],
          dailyBreakTime: employee.dailyBreakTime,
        } as InputProps
      }
      validationSchema={EmployeeSchema}
      validateOnChange
      validateOnMount
    >
      <Stack>
        <Flex>
          <InputField name="first_name" mr={1} />
          <InputField name="last_name" />
        </Flex>
        <CheckboxSelect
          name="work_days"
          label="employeeTile.editEmployee.input.workDays.label"
          columns={5}
          fields={workDayValues}
          labels={(value) => {
            return value;
          }}
          values={(value) => {
            return value;
          }}
          keys={(value) => {
            return value;
          }}
          orderBy={false}
        />

        <DefaultNumberInputField
          name="work_hours_per_day"
          label="employeeTile.editEmployee.input.workHoursPerDay.label"
          min={0}
          max={24}
        />
        <Field name="dailyBreakTime">
          {({ field, form }: FieldProps<InputProps['dailyBreakTime']>) => (
            <>
              <Text>
                {translate('companySettings.dailyBreakAmount') +
                  ': ' +
                  (field.value ?? employee.dailyBreakTime ?? 30) +
                  ' ' +
                  translate('companySettings.minutes')}
              </Text>
              <Slider
                min={15}
                max={120}
                step={5}
                defaultValue={employee.dailyBreakTime ?? 30}
                value={field.value}
                onChange={(value) => form.setFieldValue(field.name, value)}
              >
                <SliderMark value={15} mt="1" fontSize="sm">
                  15
                </SliderMark>
                <SliderMark value={120} mt="1" ml="-5" fontSize="sm">
                  120
                </SliderMark>
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </>
          )}
        </Field>
        <Checkbox
          mt={5}
          name=""
          isChecked={changePassword}
          onChange={() => setChangePassword(!changePassword)}
          borderColor="black"
        >
          employeeTile.changePassword.label
        </Checkbox>
        {changePassword && (
          <InputField
            name="password"
            label="employeeTile.password.label"
            placeholder=""
          />
        )}
        <FormikCheckbox name="needs_two_factor" borderColor="black">
          employeeTile.needsTwoFactor
        </FormikCheckbox>

        <Text fontWeight="semibold">employeeTile.editEmployee.roles.title</Text>
        {allRoles?.map((role) => (
          <Checkbox
            key={role.code}
            isChecked={!!roles?.find((r) => r.role.code === role.code)}
            onChange={() => {
              addRole({ employee, roleCode: role.code });
            }}
          >
            {`roles.${role.name}`}
          </Checkbox>
        ))}
        <Text fontWeight="semibold">employeeTile.editEmployee.departments</Text>
        <EmployeeDepartments name="departments" />
      </Stack>
    </DefaultModalDialog>
  );
};
